import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  CONSENT_APPLIED_EVENT,
  CONSENT_UPDATED_EVENT,
  getConsentRecord,
  saveConsent,
  type ConsentRecord,
} from '@/lib/consent'
import { initGA4, initMetaPixel } from '@/lib/analytics'

interface ConsentProviderProps {
  children: ReactNode
}

function applyConsent(record: ConsentRecord | null) {
  if (!record || record.revokedAt) return

  const hasNonEssentialConsent = record.categories.analytics || record.categories.marketing

  if (record.categories.analytics) {
    initGA4()
  }

  if (record.categories.marketing) {
    initMetaPixel()
  }

  if (hasNonEssentialConsent) {
    window.dispatchEvent(new CustomEvent(CONSENT_APPLIED_EVENT, { detail: record }))
  }
}

function getLegalPath(language: string, path: 'privacy' | 'cookies') {
  const locale = language.startsWith('pt') ? 'pt' : language.startsWith('es') ? 'es' : 'en'
  return `/${locale}/${path}`
}

export function ConsentProvider({ children }: ConsentProviderProps) {
  const { t, i18n } = useTranslation('common')
  const [record, setRecord] = useState<ConsentRecord | null>(() => getConsentRecord())
  const [showCustomize, setShowCustomize] = useState(false)
  const [analytics, setAnalytics] = useState(false)
  const [marketing, setMarketing] = useState(false)

  useEffect(() => {
    applyConsent(record)
  }, [record])

  useEffect(() => {
    const handleConsentUpdate = (event: Event) => {
      const nextRecord = (event as CustomEvent<ConsentRecord>).detail
      setRecord(nextRecord.revokedAt ? null : nextRecord)
    }

    window.addEventListener(CONSENT_UPDATED_EVENT, handleConsentUpdate)
    return () => window.removeEventListener(CONSENT_UPDATED_EVENT, handleConsentUpdate)
  }, [])

  const bannerVisible = !record
  const privacyPath = useMemo(() => getLegalPath(i18n.language, 'privacy'), [i18n.language])
  const cookiesPath = useMemo(() => getLegalPath(i18n.language, 'cookies'), [i18n.language])

  const acceptAll = () => {
    setRecord(saveConsent({ analytics: true, marketing: true }))
  }

  const rejectNonEssential = () => {
    setRecord(saveConsent({ analytics: false, marketing: false }))
  }

  const saveCustom = () => {
    setRecord(saveConsent({ analytics, marketing }))
  }

  return (
    <>
      {children}
      {bannerVisible && (
        <section
          aria-label={t('cookieConsent.ariaLabel')}
          className="fixed inset-x-4 bottom-4 z-[100] mx-auto max-w-4xl rounded-2xl border border-border bg-background p-4 shadow-2xl sm:p-6"
        >
          <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-start">
            <div className="space-y-3">
              <div>
                <p className="text-base font-semibold text-foreground">{t('cookieConsent.title')}</p>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">{t('cookieConsent.description')}</p>
              </div>

              {showCustomize && (
                <div className="grid gap-3 rounded-xl border border-border bg-muted/40 p-3 sm:grid-cols-3">
                  <label className="flex items-start gap-3 text-sm">
                    <Checkbox checked disabled />
                    <span>
                      <span className="block font-medium text-foreground">{t('cookieConsent.necessary.title')}</span>
                      <span className="text-muted-foreground">{t('cookieConsent.necessary.description')}</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 text-sm">
                    <Checkbox checked={analytics} onCheckedChange={(checked) => setAnalytics(checked === true)} />
                    <span>
                      <span className="block font-medium text-foreground">{t('cookieConsent.analytics.title')}</span>
                      <span className="text-muted-foreground">{t('cookieConsent.analytics.description')}</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 text-sm">
                    <Checkbox checked={marketing} onCheckedChange={(checked) => setMarketing(checked === true)} />
                    <span>
                      <span className="block font-medium text-foreground">{t('cookieConsent.marketing.title')}</span>
                      <span className="text-muted-foreground">{t('cookieConsent.marketing.description')}</span>
                    </span>
                  </label>
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                {t('cookieConsent.linksPrefix')}{' '}
                <Link to={privacyPath} className="font-medium text-primary underline-offset-4 hover:underline">
                  {t('cookieConsent.privacyLink')}
                </Link>{' '}
                {t('cookieConsent.linksMiddle')}{' '}
                <Link to={cookiesPath} className="font-medium text-primary underline-offset-4 hover:underline">
                  {t('cookieConsent.cookiesLink')}
                </Link>
                .
              </p>
            </div>

            <div className="flex flex-col gap-2 sm:min-w-56">
              <Button onClick={acceptAll}>{t('cookieConsent.acceptAll')}</Button>
              <Button variant="outline" onClick={rejectNonEssential}>{t('cookieConsent.rejectNonEssential')}</Button>
              {showCustomize ? (
                <Button variant="secondary" onClick={saveCustom}>{t('cookieConsent.savePreferences')}</Button>
              ) : (
                <Button variant="ghost" onClick={() => setShowCustomize(true)}>{t('cookieConsent.customize')}</Button>
              )}
            </div>
          </div>
        </section>
      )}
    </>
  )
}
